import { useEffect, useMemo, useState } from 'react'
import { IPVPNavigation } from '@/components/Navbar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { StatCard } from '@/pages/Organization/Analytics/components/StatCard'
import { adminApi } from '@/utils/api'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'

type CoupleRow = {
  id: string
  partnerAName?: string
  partnerBName?: string
  partnerAPhone?: string
  partnerBPhone?: string
  status?: string
  invitedAt?: string
}

export default function AdminCouplesPage() {
  const { user, logout } = useAuth()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)
  const [couples, setCouples] = useState<CoupleRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [inviteForm, setInviteForm] = useState({
    partnerAFirstName: '',
    partnerALastName: '',
    partnerAPhone: '',
    partnerAEmail: '',
    partnerBFirstName: '',
    partnerBLastName: '',
    partnerBPhone: '',
    partnerBEmail: '',
  })

  const fetchCouples = async () => {
    try {
      setError(null)
      setLoading(true)
      const res = await adminApi.couplesList({ page: 1, limit: 20 })
      const rows =
        (res as { data?: { data?: CoupleRow[] } }).data?.data ||
        (res as { data?: CoupleRow[] }).data ||
        []
      setCouples(Array.isArray(rows) ? rows : [])
    } catch (err) {
      setError((err as Error)?.message || 'Failed to load couples')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchCouples()
  }, [])

  const totals = useMemo(() => {
    const total = couples.length
    const invited = couples.filter((c) => c.status === 'invited').length
    const completed = couples.filter((c) => c.status === 'completed').length
    return { total, invited, completed }
  }, [couples])

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    setInviteError(null)
    setInviteSuccess(null)
    setInviteLoading(true)
    try {
      await adminApi.couplesInvite(inviteForm)
      setInviteSuccess('Invitation sent.')
      setInviteForm({
        partnerAFirstName: '',
        partnerALastName: '',
        partnerAPhone: '',
        partnerAEmail: '',
        partnerBFirstName: '',
        partnerBLastName: '',
        partnerBPhone: '',
        partnerBEmail: '',
      })
      await fetchCouples()
      setAddOpen(false)
    } catch (err) {
      setInviteError((err as Error)?.message || 'Unable to invite couple')
    } finally {
      setInviteLoading(false)
    }
  }

  const handleResend = async (id: string) => {
    setResendingId(id)
    setError(null)
    try {
      await adminApi.couplesResendInvite(id)
      await fetchCouples()
    } catch (err) {
      setError((err as Error)?.message || 'Unable to resend invite')
    } finally {
      setResendingId(null)
    }
  }

  const handleSignOut = () => {
    logout()
    window.location.href = '/login'
  }

  return (
    <div className='w-full min-h-screen bg-[#F8F9FA] px-4 py-6 md:px-6 lg:px-8 space-y-6'>
      <div className='bg-white rounded-lg shadow-sm p-4'>
        <IPVPNavigation
          title=' Couples'
          infoText='Invite couples, resend links, and track onboarding.'
          showLocation={false}
          userName={userName}
          userInitials={userInitials}
          onSignOut={handleSignOut}
          only
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        <StatCard title='Total Couples' value={totals.total.toLocaleString()} subtitle='All time' trend='' />
        <StatCard title='Invited' value={totals.invited.toLocaleString()} subtitle='Pending onboarding' trend='' />
        <StatCard title='Completed' value={totals.completed.toLocaleString()} subtitle='Finished onboarding' trend='' />
      </div>

      <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-gray-900'>Couple Invites</h3>
          <Sheet open={addOpen} onOpenChange={setAddOpen}>
            <SheetTrigger asChild>
              <Button className='bg-gray-900 hover:bg-gray-800 text-white text-sm'>Invite Couple</Button>
            </SheetTrigger>
            <SheetContent side='right' className='w-full sm:w-[420px] md:w-[500px]'>
              <SheetHeader>
                <SheetTitle>Invite Couple</SheetTitle>
              </SheetHeader>
              <form onSubmit={handleInvite} className='mt-4 space-y-3 overflow-y-auto max-h-[80vh] pr-1'>
                <p className='text-xs text-gray-600'>Both partners will receive the invite via WhatsApp/email.</p>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Partner A First Name</label>
                    <input
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                      required
                      value={inviteForm.partnerAFirstName}
                      onChange={(e) => setInviteForm({ ...inviteForm, partnerAFirstName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Partner A Last Name</label>
                    <input
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                      required
                      value={inviteForm.partnerALastName}
                      onChange={(e) => setInviteForm({ ...inviteForm, partnerALastName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Partner A Phone</label>
                    <PhoneNumberInput
                      value={inviteForm.partnerAPhone}
                      onChange={(value) => setInviteForm({ ...inviteForm, partnerAPhone: value })}
                      placeholder='Phone number'
                      required
                      containerClassName='border-gray-300'
                      inputClassName='py-0'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Partner A Email</label>
                    <input
                      type='email'
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                      value={inviteForm.partnerAEmail}
                      onChange={(e) => setInviteForm({ ...inviteForm, partnerAEmail: e.target.value })}
                    />
                  </div>
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-gray-100'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Partner B First Name</label>
                    <input
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                      required
                      value={inviteForm.partnerBFirstName}
                      onChange={(e) => setInviteForm({ ...inviteForm, partnerBFirstName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Partner B Last Name</label>
                    <input
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                      required
                      value={inviteForm.partnerBLastName}
                      onChange={(e) => setInviteForm({ ...inviteForm, partnerBLastName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Partner B Phone</label>
                    <PhoneNumberInput
                      value={inviteForm.partnerBPhone}
                      onChange={(value) => setInviteForm({ ...inviteForm, partnerBPhone: value })}
                      placeholder='Phone number'
                      required
                      containerClassName='border-gray-300'
                      inputClassName='py-0'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Partner B Email</label>
                    <input
                      type='email'
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                      value={inviteForm.partnerBEmail}
                      onChange={(e) => setInviteForm({ ...inviteForm, partnerBEmail: e.target.value })}
                    />
                  </div>
                </div>

                {(inviteError || inviteSuccess) && (
                  <div
                    className={`text-sm rounded-lg px-4 py-3 ${
                      inviteError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                    }`}
                  >
                    {inviteError || inviteSuccess}
                  </div>
                )}

                <Button type='submit' disabled={inviteLoading} className='w-full bg-gray-900 hover:bg-gray-800'>
                  {inviteLoading ? 'Sending...' : 'Send Invite'}
                </Button>
              </form>
            </SheetContent>
          </Sheet>
        </div>

        {error && <p className='text-sm text-red-600 mb-3'>{error}</p>}

        <div className='overflow-x-auto'>
          <div className='grid grid-cols-6 gap-2 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-700 text-start'>
            <span>Partner A</span>
            <span>Partner B</span>
            <span>Status</span>
            <span>Invited</span>
            <span>Phones</span>
            <span className='text-right'>Actions</span>
          </div>
          {loading && <p className='text-sm text-gray-600 p-4'>Loading couples...</p>}
          {!loading && couples.length === 0 && (
            <p className='text-sm text-gray-600 p-4'>No couples found.</p>
          )}
          {!loading &&
            couples.map((c) => (
              <div
                key={c.id}
                className='grid grid-cols-6 gap-2 px-4 py-3 text-sm text-gray-800 border-t border-gray-100 items-center text-start'
              >
                <span className='font-semibold'>{c.partnerAName || 'Partner A'}</span>
                <span>{c.partnerBName || 'Partner B'}</span>
                <span className='capitalize'>{c.status || 'invited'}</span>
                <span>{c.invitedAt ? new Date(c.invitedAt).toLocaleDateString() : '—'}</span>
                <span className='text-xs text-gray-600'>
                  {c.partnerAPhone || '—'} / {c.partnerBPhone || '—'}
                </span>
                <div className='flex justify-end gap-2 text-xs'>
                  <button
                    className='px-3 py-1 rounded-md bg-orange-50 text-orange-600 hover:bg-orange-100 disabled:opacity-50'
                    onClick={() => handleResend(c.id)}
                    disabled={resendingId === c.id}
                  >
                    {resendingId === c.id ? 'Resending...' : 'Resend Invite'}
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}
